/**
 * The subset of a status-mapping row that validation cares about. `category`
 * is a string here (the caller's enum value widens to string) so this helper
 * has no dependency on the generated GraphQL enum.
 */
export type StatusMappingDraft = {
  category: string;
  internalName: string;
  externalName: string;
};

/**
 * Result of validating the status-mapping editor rows.
 */
export type StatusMappingValidation = {
  /**
   * The `${category}::${internalName}` keys that appear on more than one row
   * (ignoring rows with a blank internal name). Saving is blocked while this is
   * non-empty because the backend enforces uniqueness on
   * `(apiUserId, category, internalName)`.
   */
  duplicateKeys: Set<string>;
  /** True when any row has a blank (whitespace-only) internal or external name. */
  hasEmptyField: boolean;
};

/**
 * Validates the status-mapping editor rows, detecting duplicate
 * `(category, internalName)` pairs and blank name fields. Names are compared
 * trimmed, matching what the save path sends to the backend.
 *
 * @param rows The current editor rows.
 * @returns The set of duplicate keys and whether any field is blank.
 */
export const validateStatusMappingRows = (
  rows: readonly StatusMappingDraft[],
): StatusMappingValidation => {
  const seen = new Set<string>();
  const duplicateKeys = new Set<string>();
  let hasEmptyField = false;

  for (const row of rows) {
    const internalName = row.internalName.trim();
    const externalName = row.externalName.trim();
    if (internalName === "" || externalName === "") {
      hasEmptyField = true;
    }
    if (internalName === "") {
      continue;
    }
    const key = `${row.category}::${internalName}`;
    if (seen.has(key)) {
      duplicateKeys.add(key);
    }
    seen.add(key);
  }

  return { duplicateKeys, hasEmptyField };
};
